import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Grid, Typography, Button, TextField, Select, MenuItem,
  Avatar, ListItemText, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Stack, Divider, Chip,
} from '@mui/material';
import AddRoundedIcon          from '@mui/icons-material/AddRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import DeleteOutlineIcon       from '@mui/icons-material/DeleteOutline';
import SaveRoundedIcon         from '@mui/icons-material/SaveRounded';
import axios from 'axios';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs }        from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/de';
import { useGrowSetup }        from '../../GrowSetupContext';
import { useConfig }           from '../../components/ConfigContext';
import SmallConfigLicht        from '../../components/SmallConfigLicht';
import SmallConfigTemperatur   from '../../components/SmallConfigTemperatur';
import SmallConfigAbluft       from '../../components/SmallConfigAbluft';
import { useDropzone }         from 'react-dropzone';

function SectionCard({ children, title, delay = 0 }) {
  return (
    <Box
      sx={{
        borderRadius: '16px',
        background: 'rgba(15,28,15,0.75)',
        border: '1px solid rgba(118,196,66,0.12)',
        backdropFilter: 'blur(16px)',
        overflow: 'hidden',
        animation: `fadeSlideUpCard 0.5s cubic-bezier(0.22,1,0.36,1) ${delay}ms both`,
      }}
    >
      {title && (
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(118,196,66,0.1)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#76c442', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.7rem' }}>
            {title}
          </Typography>
        </Box>
      )}
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </Box>
  );
}

export default function ConfigPage() {
  const { grow_setup }       = useGrowSetup();
  const { state, saveConfig } = useConfig();
  const configReady = typeof state.config.sun_rise === 'string';
  const [presets,          setPresets]   = useState({});
  const [selectedPreset,   setSelected]  = useState('');
  const [presetName,       setName]      = useState('');
  const [deleteDialogOpen, setDelDlg]    = useState(false);
  const [presetToDelete,   setDelTarget] = useState(null);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    onDrop: (files) => {
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = reader.result;
        const cut = b64.split(',')[1];
        setPresets(p => ({ ...p, [selectedPreset]: { ...p[selectedPreset], avatar: b64 } }));
        axios.post('/upload_avatar_preset', { base64String_cut: cut, preset_id: presets[selectedPreset]?.id }).catch(console.error);
      };
      reader.readAsDataURL(files[0]);
    },
  });

  const fetchPresets = () => {
    axios.post('/get_presets', {}).then(r => setPresets(r.data || {})).catch(console.error);
  };

  useEffect(() => { fetchPresets(); }, []);

  const savePreset = async () => {
    if (!presetName.trim()) return;
    try {
      await axios.post('/save_preset', { name: presetName, data: state });
      setName('');
      fetchPresets();
    } catch (e) { console.error(e); }
  };

  const deletePreset = (id) => {
    axios.delete(`/delete_preset/${id}`).then(fetchPresets).catch(console.error);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Box>
        {/* Preset Manager */}
        <SectionCard title="Presets" delay={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" flexWrap="wrap">
            <Select
              value={selectedPreset}
              onChange={e => setSelected(e.target.value)}
              displayEmpty size="small"
              sx={{
                minWidth: 220,
                background: 'rgba(118,196,66,0.07)',
                borderRadius: '10px',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(118,196,66,0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#76c442' },
              }}
            >
              <MenuItem value="" disabled><Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>Preset auswählen...</Typography></MenuItem>
              {Object.entries(presets).map(([id, p]) => (
                <MenuItem value={id} key={id}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar {...getRootProps()} src={p.avatar || ''} sx={{ width: 28, height: 28 }}>
                      <ConstructionRoundedIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                    <input {...getInputProps()} />
                    <ListItemText primary={p.name} />
                    {p.is_system_default && <Chip label="Default" size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />}
                  </Box>
                  {!p.is_system_default && (
                    <DeleteOutlineIcon
                      color="error" sx={{ ml: 1, cursor: 'pointer', fontSize: 18 }}
                      onClick={e => { e.stopPropagation(); setDelTarget(p.id); setDelDlg(true); }}
                    />
                  )}
                </MenuItem>
              ))}
              <MenuItem value={-1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 28, height: 28, background: 'rgba(118,196,66,0.15)' }}>
                    <AddRoundedIcon sx={{ fontSize: 14, color: '#76c442' }} />
                  </Avatar>
                  <Typography variant="body2" sx={{ color: '#76c442' }}>Preset hinzufügen</Typography>
                </Box>
              </MenuItem>
            </Select>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                label="Preset-Name" size="small" value={presetName}
                onChange={e => setName(e.target.value)}
                sx={{ width: 200 }}
              />
              <Button
                variant="contained" onClick={savePreset}
                startIcon={<SaveRoundedIcon />}
                disabled={!presetName.trim()}
              >
                Speichern
              </Button>
            </Stack>
          </Stack>
        </SectionCard>

        <Divider sx={{ my: 3, borderColor: 'rgba(118,196,66,0.1)' }} />

        {/* Config cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <SectionCard title="Licht" delay={100}>
              {configReady ? <SmallConfigLicht is_managed /> : <Box sx={{ py: 6, display:'flex', justifyContent:'center' }}><span style={{ color:'rgba(255,255,255,0.3)' }}>Laden…</span></Box>}
            </SectionCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <SectionCard title="Temperatur" delay={160}>
              {configReady ? <SmallConfigTemperatur is_managed /> : <Box sx={{ py: 6, display:'flex', justifyContent:'center' }}><span style={{ color:'rgba(255,255,255,0.3)' }}>Laden…</span></Box>}
            </SectionCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <SectionCard title="Abluft" delay={220}>
              {configReady ? <SmallConfigAbluft is_managed /> : <Box sx={{ py: 6, display:'flex', justifyContent:'center' }}><span style={{ color:'rgba(255,255,255,0.3)' }}>Laden…</span></Box>}
            </SectionCard>
          </Grid>
        </Grid>

        {/* Floating save button */}
        <Box sx={{ position: 'fixed', bottom: 24, right: 28, zIndex: 999 }}>
          <Button
            variant="contained" onClick={saveConfig}
            startIcon={<SaveRoundedIcon />}
            sx={{
              px: 4, py: 1.5, borderRadius: '50px', fontWeight: 700,
              boxShadow: '0 8px 24px rgba(118,196,66,0.45)',
              fontSize: '0.95rem',
            }}
          >
            Alles speichern
          </Button>
        </Box>

        {/* Delete confirm dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDelDlg(false)}
          PaperProps={{ sx: { background: 'rgba(8,20,9,0.97)', border: '1px solid rgba(118,196,66,0.15)', borderRadius: '16px', backdropFilter: 'blur(24px)' } }}
        >
          <DialogTitle sx={{ color: '#ef4444' }}>Preset löschen</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Möchtest du das Preset wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDelDlg(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>Abbrechen</Button>
            <Button
              variant="contained" color="error"
              onClick={() => { deletePreset(presetToDelete); setDelDlg(false); }}
            >Löschen</Button>
          </DialogActions>
        </Dialog>
      </Box>

      <style>{`
        @keyframes fadeSlideUpCard {
          from { opacity:0; transform:translateY(20px); }
          to   { opacity:1; transform:translateY(0);    }
        }
      `}</style>
    </LocalizationProvider>
  );
}
